import { ReactNode } from 'react';

import { TyreReading } from '@/services/api.types';
import doubleTyresAxle from './assets/double-tyres-axle.svg';
import singleTyresAxle from './assets/single-tyres-axle.svg';
import tyreLeft from './assets/tyre-left.svg';
import tyrePairInnerLeft from './assets/tyre-pair-inner-left.svg';
import tyrePairInnerRight from './assets/tyre-pair-inner-right.svg';
import tyrePairOuterLeft from './assets/tyre-pair-outer-left.svg';
import tyrePairOuterRight from './assets/tyre-pair-outer-right.svg';
import tyreRight from './assets/tyre-right.svg';
import { PressureLabel, TemperatureLabel } from './TyreLabel';
import TyrePopover from './TyrePopover';
import { AxleData, isDualTyre } from './utils';

const TYRE_ICONS = {
    left: { single: tyreLeft, outer: tyrePairOuterLeft, inner: tyrePairInnerLeft },
    right: { single: tyreRight, outer: tyrePairOuterRight, inner: tyrePairInnerRight },
};

interface AxelCellProps {
    children: ReactNode;
    className?: string;
    isLast: boolean;
}

interface InfoRowProps {
    tyre?: TyreReading;
    icon: string;
    side: 'left' | 'right';
    axleLabel: string;
    assetId: string;
}

interface AxelSideColumnProps {
    axle: AxleData;
    side: 'left' | 'right';
    assetId: string;
    isLast: boolean;
}

interface AxelRowProps {
    axle: AxleData;
    assetId: string;
    isLast: boolean;
}

const AxelCell = ({ children, className, isLast }: AxelCellProps) => (
    <div
        className={
            `display-flex flex-column justify-content-center gap-2 padding-10 ` +
            `padding-x-0-xs ${!isLast && 'border border-bottom-only border-color-lightest'} ${className}`
        }
    >
        {children}
    </div>
);

const AxleCenter = ({ axle, isLast }: { axle: AxleData; isLast: boolean }) => (
    <AxelCell className="align-items-center" isLast={isLast}>
        <div className="text-size-14 text-normal text-color-black">{axle.label}</div>
        <img
            className="max-width-80 hidden-xs"
            src={isDualTyre(axle) ? doubleTyresAxle : singleTyresAxle}
            alt={`axle ${axle.label}`}
        />
    </AxelCell>
);

const InfoRow = ({ tyre, icon, side, axleLabel, assetId }: InfoRowProps) => {
    const tyreIcon = <img src={icon} alt="tyre" className={side === 'left' ? 'margin-left-5' : 'margin-right-5'} />;

    return (
        <div className={`display-flex align-items-center gap-2 ${side === 'right' ? 'justify-content-end' : ''}`}>
            {side === 'right' && tyreIcon}
            <TyrePopover tyre={tyre} axleLabel={axleLabel} assetId={assetId} side={side}>
                <div className="display-flex align-items-center gap-2 cursor-pointer">
                    <PressureLabel pressure={tyre?.pressure} status={tyre?.pressure_status} />
                    <TemperatureLabel temperature={tyre?.temperature} status={tyre?.temperature_status} />
                </div>
            </TyrePopover>
            {side === 'left' && tyreIcon}
        </div>
    );
};

const AxleSideColumn = ({ axle, side, assetId, isLast }: AxelSideColumnProps) => {
    const dual = isDualTyre(axle);
    const outer = side === 'left' ? axle.tyres.leftOutside : axle.tyres.rightOutside;
    const inner = side === 'left' ? axle.tyres.leftInside : axle.tyres.rightInside;
    const icons = TYRE_ICONS[side];

    return (
        <AxelCell isLast={isLast}>
            {!dual ? (
                <InfoRow tyre={outer} icon={icons.single} side={side} axleLabel={axle.label} assetId={assetId} />
            ) : (
                <>
                    <InfoRow tyre={outer} icon={icons.outer} side={side} axleLabel={axle.label} assetId={assetId} />
                    <InfoRow tyre={inner} icon={icons.inner} side={side} axleLabel={axle.label} assetId={assetId} />
                </>
            )}
        </AxelCell>
    );
};

const AxleRow = ({ axle, assetId, isLast }: AxelRowProps) => (
    <>
        <AxleSideColumn axle={axle} side="left" assetId={assetId} isLast={isLast} />
        <AxleCenter axle={axle} isLast={isLast} />
        <AxleSideColumn axle={axle} side="right" assetId={assetId} isLast={isLast} />
    </>
);

export default AxleRow;
